import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'

import { Props as SlideProps, Slide } from './Slide'

export interface Props {
  claim?: string
  cta?: ButtonProps
  description?: string
  images?: SlideProps[]
  label?: string
  title?: string
  variant?: Variant
}

export const VisualService = memo(function VisualService({
  claim,
  cta,
  description,
  images,
  label,
  title,
  variant = 'default',
}: Props) {
  if (!images) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const halfItems = Math.ceil(images.length / 2)

  return (
    <Container variant={variant}>
      <Head>
        {label ? <Label tag="h2" text={label} /> : null}

        {claim ? <Claim text={claim} /> : null}
      </Head>

      <Images row space="between" wrap>
        <LeftSide>
          {images.slice(0, halfItems).map((item, index) => (
            <Slide key={index} {...item} />
          ))}
        </LeftSide>

        <RightSide>
          {images.slice(halfItems).map((item, index) => (
            <Slide key={index} variant="invert" {...item} />
          ))}
        </RightSide>
      </Images>

      <Bottom>
        {title ? <Title tag="h3" text={title} /> : null}

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}

        {cta ? (
          <FadeInUp>
            <CTA variant="flat-invert" {...cta} />
          </FadeInUp>
        ) : null}
      </Bottom>
    </Container>
  )
})

const Head = styled.div`
  padding: 0 5vw;

  @media (max-width: 1199px) {
    padding: 0 2rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};

  @media (max-width: 1199px) {
    margin-bottom: 1.5rem;
  }
`

const Claim = styled(AnimatedTitle)`
  ${styles.claim}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};

  @media (max-width: 1679px) {
    max-width: 68vw;
  }

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Images = styled(FlexBox)`
  margin-top: 7.5rem;
  padding: 0 1.5rem;

  @media (max-width: 1199px) {
    margin-top: 0;
    padding: 0 1rem;
  }
`

const LeftSide = styled.div`
  width: 46.667vw;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const RightSide = styled.div`
  width: 46.667vw;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const Bottom = styled.div`
  max-width: 53rem;
  margin: 7.5rem auto 0;
  text-align: center;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 5.25rem;
    padding: 0 2rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.medium}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const Description = styled.div`
  ${styles.description}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  margin-top: 2rem;
`

const CTA = styled(Button)`
  margin-top: 2rem;
`

const Container = styled.section<ContainerProps>`
  background: ${({ theme }) => theme.colors.variants.primaryLight};
  padding: 9.75rem 0;

  @media (max-width: 1199px) {
    padding: 5.25rem 0;
  }

  ${({ theme, variant }) => {
    switch (variant) {
      case 'compact':
        return css`
          background: ${theme.colors.variants.neutralLight2};
          padding: 0;

          ${LeftSide},
          ${RightSide} {
            div {
              color: ${rgba(theme.colors.variants.primaryDark, 0.5)};
            }
          }

          @media (max-width: 1199px) {
            margin-top: 1.5rem;
            padding: 0;
          }
        `
    }
  }}
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'compact'
